import type { GaragePickingListItemRecord } from '@navio/nsdk';
import { type InputData, noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorPageInternal,
  filterUnitFor,
  NavioPage,
  NoddiAsyncConfirmationDialog,
  NoddiTable,
  type TableAction,
  useFilterUnits,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { formatHotelBinLocation } from 'noddi-util';
import { useMemo, useState } from 'react';
import { useStorageFacilityOptions } from '../../hooks/filters/StorageFacility/useStorageFacilitiesFilter';
import { copyLicensePlateAction } from '../CarDetails/copyLicensePlateAction';
import { customerInfoSearchLabel } from '../UserGroups/customerInfo';
import { getGaragePickingListColumns } from './columns';
import type { GaragePickingListRoutes } from './types';
import { prepareForGarageInput, usePrepareForGarage } from './usePrepareForGarage';

interface Props {
  routes: GaragePickingListRoutes;
  storageFacilityId?: number;
  serviceDepartmentIds?: number[];
  embedded?: boolean;
  fromDate?: string;
  toDate?: string;
}

// Partial: the facility is a filter until one is picked.
type GaragePickingListFilterInput = Partial<InputData<typeof URLKeys.getGaragePickingList>>;

const garagePickingListFilterUnit = filterUnitFor<GaragePickingListFilterInput>();

const garagePickingListDefaultDateRange = (): { from: Date; to: Date } => {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(from.getDate() + 1);
  return { from, to };
};

const matchesSearch = (row: GaragePickingListItemRecord, search: string): boolean => {
  if (!search) {
    return true;
  }
  const haystack = [
    row.userGroup ? customerInfoSearchLabel(row.userGroup) : '',
    row.licensePlateNumber,
    formatHotelBinLocation(row.location),
    row.storageObjectGroupIdentifier,
    row.workstation?.name
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(search);
};

export const GaragePickingListPage = ({
  routes,
  storageFacilityId: scopedStorageFacilityId,
  serviceDepartmentIds,
  embedded = false,
  fromDate: fromDateOverride,
  toDate: toDateOverride
}: Props) => {
  const { pagination, setPagination } = usePaginationQueryParam({ initialPageSize: 100 });
  const storageFacilities = useStorageFacilityOptions({ serviceDepartmentIds });
  const [pendingItem, setPendingItem] = useState<GaragePickingListItemRecord | null>(null);
  const { mutateAsync, isPending: isPreparing } = usePrepareForGarage();

  const filters = useFilterUnits([
    // One facility only — API is per-facility; do not fan out / client-merge.
    scopedStorageFacilityId != null
      ? garagePickingListFilterUnit.locked({ storageFacilityId: scopedStorageFacilityId })
      : garagePickingListFilterUnit.singleSelect('storageFacilityId', {
          title: 'Storage facility',
          description: 'Storage facility whose hotel bins to pick from. Select one facility.',
          source: storageFacilities
        }),
    garagePickingListFilterUnit.boolean('isPrepared', {
      title: 'Prepared',
      description: 'Show wheel sets that are already on garage staging, or still to prepare.'
    }),
    fromDateOverride != null && toDateOverride != null
      ? garagePickingListFilterUnit.locked({ fromDate: fromDateOverride, toDate: toDateOverride })
      : garagePickingListFilterUnit.toolbarDateRange(
          { from: 'fromDate', to: 'toDate' },
          { defaults: garagePickingListDefaultDateRange }
        )
  ]);
  const { storageFacilityId, ...filterQuery } = filters.apiInput;
  const needsFacilitySelection = storageFacilityId == null;

  const {
    data,
    isPending: isPickingPending,
    isFetching: isPickingFetching,
    error: pickingError
  } = noddiAsync.useGet({
    type: URLKeys.getGaragePickingList,
    input: { ...filterQuery, storageFacilityId },
    queryConfig: {
      enabled: storageFacilityId != null
    }
  });

  const filteredRows = useMemo(() => {
    const search = pagination.search.trim().toLowerCase();
    return (data ?? []).filter((row) => matchesSearch(row, search));
  }, [data, pagination.search]);

  const pagedRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return filteredRows.slice(start, start + pagination.pageSize);
  }, [filteredRows, pagination.pageIndex, pagination.pageSize]);

  const error = pickingError;
  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }

  const actions: TableAction<GaragePickingListItemRecord>[] = [
    copyLicensePlateAction((row: GaragePickingListItemRecord) => row.licensePlateNumber),
    {
      label: 'Prepare',
      actionType: 'send',
      disabled: (row) => row.isPrepared || isPreparing,
      onClick: (row) => setPendingItem(row)
    }
  ];

  const table = (
    <>
      <NoddiTable
        fillHeight={!embedded}
        data={needsFacilitySelection ? [] : pagedRows}
        columns={getGaragePickingListColumns()}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={needsFacilitySelection ? 0 : filteredRows.length}
        isPending={!needsFacilitySelection && isPickingPending}
        isFetching={!needsFacilitySelection && isPickingFetching}
        getRowId={(row) => row.storageObjectGroupIdentifier}
        searchPlaceholder='Search plate, customer or location'
        emptyStateText={
          needsFacilitySelection
            ? 'Select a storage facility to load the garage picking list.'
            : 'No wheel sets on this garage picking list.'
        }
        getUrlPath={(row) => ({
          url: routes.detail.getPath({
            identifier: row.storageObjectGroupIdentifier,
            storageFacilityId: row.location?.storageFacilityId ?? storageFacilityId,
            'from-date': filterQuery.fromDate,
            'to-date': filterQuery.toDate
          })
        })}
        filters={filters}
        actions={actions}
      />
      <NoddiAsyncConfirmationDialog
        open={pendingItem != null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingItem(null);
          }
        }}
        title='Prepare for garage'
        description='Move this wheel set from its hotel bin onto the garage staging unit so it is ready before the customer arrives.'
        confirmText='Prepare'
        confirmActionLoading={isPreparing}
        confirmAction={async () => {
          if (pendingItem == null) {
            return;
          }
          await mutateAsync(prepareForGarageInput(pendingItem));
          setPendingItem(null);
        }}
      />
    </>
  );

  if (embedded) {
    return table;
  }

  return (
    <NavioPage
      title='Garage picking list'
      description='Stored wheel sets for stationary bookings today and tomorrow. Pick from hotel bins and prepare in the garage.'
      variant='secondary'
      fillHeight
      breadcrumbs={[{ title: 'Garage picking list' }]}
    >
      {table}
    </NavioPage>
  );
};
